import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Plus, Trash2, Edit3, BookOpen, FileText, AlertCircle, CheckCircle, Globe, PenTool, Sparkles, ChevronDown } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { calculateTotalWords, validateNovelOutline, autoFixOutline, STORY_STRUCTURES, NOVEL_GENRES, WRITING_STYLES, LANGUAGES } from './novelUtils'

const NovelOutlinePage = ({ query, outline, onOutlineChange, onStartGeneration, onGenerateOutline, onBack, isLoading, error, llmService }) => {
  const [editingSection, setEditingSection] = useState(null)
  const [newSectionTitle, setNewSectionTitle] = useState('')
  const [showAddSection, setShowAddSection] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES.INDONESIAN)
  const [selectedGenre, setSelectedGenre] = useState(NOVEL_GENRES.CONTEMPORARY)
  const [selectedWritingStyle, setSelectedWritingStyle] = useState(WRITING_STYLES.NARRATIVE)
  const [selectedPlotStructure, setSelectedPlotStructure] = useState('THREE_ACT')
  const [showStructureInfo, setShowStructureInfo] = useState(false)

  // Auto-fix outline if it exists but doesn't meet requirements
  React.useEffect(() => {
    if (outline && outline.length > 0) {
      const validation = validateNovelOutline(outline)
      if (!validation.isValid && validation.adjustedOutline) {
        onOutlineChange(validation.adjustedOutline)
      }
    }
  }, [outline, onOutlineChange])

  // Validation
  const validation = validateNovelOutline(outline)
  const hasOutline = outline && outline.length > 0

  // Handle outline generation
  const handleGenerateOutline = async () => {
    const novelConfig = {
      language: selectedLanguage,
      genre: selectedGenre,
      writingStyle: selectedWritingStyle,
      plotStructure: selectedPlotStructure
    }
    
    const generatedOutline = await onGenerateOutline(novelConfig)
    
    // Auto-fix the outline to ensure it meets validation requirements
    if (generatedOutline) {
      const fixedOutline = autoFixOutline(generatedOutline)
      onOutlineChange(fixedOutline)
    }
  }

  // Handle start generation
  const handleStartGeneration = () => {
    const novelConfig = {
      language: selectedLanguage,
      genre: selectedGenre,
      writingStyle: selectedWritingStyle,
      plotStructure: selectedPlotStructure
    }
    
    onStartGeneration(outline, novelConfig)
  }

  // Handle section editing
  const handleEditSection = (sectionId) => {
    setEditingSection(sectionId)
  }

  const handleSaveSection = (sectionId, newTitle, newDescription) => {
    const updatedOutline = outline.map(section => 
      section.id === sectionId 
        ? { ...section, title: newTitle, description: newDescription }
        : section
    )
    onOutlineChange(updatedOutline)
    setEditingSection(null)
  }

  const handleDeleteSection = (sectionId) => {
    const updatedOutline = outline.filter(section => section.id !== sectionId)
    onOutlineChange(updatedOutline)
  }

  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return
    
    const newSection = {
      id: `bagian_${outline.length + 1}`,
      title: newSectionTitle,
      description: 'Deskripsi bagian baru',
      minWords: 8000,
      maxWords: 12000,
      keyEvents: [],
      characters: [],
      plotPoints: ''
    }
    
    onOutlineChange([...outline, newSection])
    setNewSectionTitle('')
    setShowAddSection(false)
  }

  const selectedStructure = STORY_STRUCTURES[selectedPlotStructure]

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className={cn(buttonVariants.secondary, "flex items-center")}
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Kembali
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <BookOpen className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-text-primary">Novel Agent</h1>
                  <p className="text-sm text-text-secondary">Buat novel berkualitas tinggi dengan struktur plot profesional</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Topic Display */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Topik Novel</h2>
          <p className="text-text-secondary text-lg break-words">"<span className="text-text-primary font-medium">{query}</span>"</p>
        </div>

        {/* Novel Configuration */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <h3 className="text-lg font-semibold text-text-primary mb-6 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
            Konfigurasi Novel
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plot Structure Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <FileText className="w-4 h-4 inline mr-2" />
                Struktur Plot Cerita
              </label>
              <div className="relative">
                <select
                  value={selectedPlotStructure}
                  onChange={(e) => setSelectedPlotStructure(e.target.value)}
                  className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                >
                  {Object.entries(STORY_STRUCTURES).map(([key, structure]) => (
                    <option key={key} value={key}>
                      {structure.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
              </div>
              
              {/* Structure Info */}
              <div className="mt-2">
                <button
                  onClick={() => setShowStructureInfo(!showStructureInfo)}
                  className="text-sm text-purple-400 hover:text-purple-300 flex items-center"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Info Struktur
                </button>
                
                {showStructureInfo && selectedStructure && (
                  <div className="mt-3 p-4 bg-bg-tertiary rounded-lg border border-border-secondary">
                    <h4 className="font-medium text-text-primary mb-2">{selectedStructure.name}</h4>
                    <p className="text-sm text-text-secondary mb-3">{selectedStructure.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-text-primary">Bagian-bagian:</p>
                      <ul className="text-sm text-text-secondary space-y-1">
                        {selectedStructure.sections.map((section, index) => (
                          <li key={index} className="flex">
                            <span className="font-medium mr-2">{index + 1}.</span>
                            <span><strong>{section.name}:</strong> {section.description}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Genre Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <BookOpen className="w-4 h-4 inline mr-2" />
                Genre Novel
              </label>
              <div className="relative">
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                >
                  {Object.entries(NOVEL_GENRES).map(([key, genre]) => (
                    <option key={key} value={genre}>
                      {genre}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
              </div>
            </div>

            {/* Writing Style Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <PenTool className="w-4 h-4 inline mr-2" />
                Gaya Penulisan
              </label>
              <div className="relative">
                <select
                  value={selectedWritingStyle}
                  onChange={(e) => setSelectedWritingStyle(e.target.value)}
                  className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                >
                  {Object.entries(WRITING_STYLES).map(([key, style]) => (
                    <option key={key} value={style}>
                      {style}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
              </div>
            </div>

            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                Bahasa
              </label>
              <div className="relative">
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                >
                  <option value={LANGUAGES.INDONESIAN}>Indonesia</option>
                  <option value={LANGUAGES.ENGLISH}>English</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Generate Outline Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleGenerateOutline}
              disabled={isLoading}
              className={cn(
                buttonVariants.primary,
                "flex items-center px-6 py-3",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Membuat Outline...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {hasOutline ? 'Regenerate Outline' : 'Generate Outline'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-8">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-400 font-medium">Error</span>
            </div>
            <p className="text-red-300 mt-2">{error}</p>
          </div>
        )}

        {/* Outline Display */}
        {hasOutline && (
          <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-text-primary flex items-center">
                <FileText className="w-5 h-5 mr-2 text-purple-500" />
                Outline Novel
              </h3>
              <div className="flex items-center space-x-2">
                {validation.isValid ? (
                  <div className="flex items-center text-emerald-400">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span className="text-sm">Valid</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-400">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    <span className="text-sm">Perlu Perbaikan</span>
                  </div>
                )}
              </div>
            </div>

            {/* Validation Errors */}
            {!validation.isValid && validation.errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <h4 className="text-red-400 font-medium mb-2">Masalah yang perlu diperbaiki:</h4>
                <ul className="text-red-300 text-sm space-y-1">
                  {validation.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Outline Sections */}
            <div className="space-y-4">
              {outline.map((section, index) => (
                <OutlineSection
                  key={section.id}
                  section={section}
                  index={index}
                  isEditing={editingSection === section.id}
                  onEdit={() => handleEditSection(section.id)}
                  onSave={handleSaveSection}
                  onDelete={() => handleDeleteSection(section.id)}
                  onCancel={() => setEditingSection(null)}
                />
              ))}
            </div>

            {/* Add Section */}
            {showAddSection ? (
              <div className="mt-6 bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
                <h4 className="text-text-primary font-medium mb-3">Tambah Bagian Baru</h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    placeholder="Judul bagian..."
                    className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleAddSection}
                      disabled={!newSectionTitle.trim()}
                      className={cn(
                        buttonVariants.primary,
                        "text-sm flex items-center",
                        !newSectionTitle.trim() && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Tambah
                    </button>
                    <button
                      onClick={() => setShowAddSection(false)}
                      className={cn(buttonVariants.secondary, "text-sm flex items-center")}
                    >
                      Batal
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <button
                  onClick={() => setShowAddSection(true)}
                  className={cn(buttonVariants.secondary, "flex items-center")}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Bagian
                </button>
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        {hasOutline && (
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">Ringkasan Novel</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-text-tertiary">Total Bagian:</span>
                    <p className="font-medium text-text-primary">{validation.chapterCount}</p>
                  </div>
                  <div>
                    <span className="text-text-tertiary">Estimasi Kata:</span>
                    <p className="font-medium text-text-primary">{validation.estimatedWords?.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-text-tertiary">Estimasi Halaman:</span>
                    <p className="font-medium text-text-primary">{validation.estimatedPages}</p>
                  </div>
                  <div>
                    <span className="text-text-tertiary">Status:</span>
                    <p className={cn(
                      "font-medium",
                      validation.isValid ? "text-emerald-400" : "text-red-400"
                    )}>
                      {validation.isValid ? "Siap" : "Perlu Perbaikan"}
                    </p>
                  </div>
                </div>
              </div>
              
              {validation.isValid && (
                <button
                  onClick={handleStartGeneration}
                  disabled={isLoading}
                  className={cn(
                    buttonVariants.primary,
                    "flex items-center px-6 py-3",
                    isLoading && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Mulai Tulis Novel
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Outline Section Component
const OutlineSection = ({ section, index, isEditing, onEdit, onSave, onDelete, onCancel }) => {
  const [editTitle, setEditTitle] = useState(section.title)
  const [editDescription, setEditDescription] = useState(section.description)

  const handleSave = () => {
    onSave(section.id, editTitle, editDescription)
  }

  if (isEditing) {
    return (
      <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
        <div className="space-y-3">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            rows={3}
            className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              className={cn(buttonVariants.primary, "text-sm flex items-center")}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Simpan
            </button>
            <button
              onClick={onCancel}
              className={cn(buttonVariants.secondary, "text-sm flex items-center")}
            >
              Batal
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <span className="text-sm font-medium text-purple-400">Bagian {index + 1}</span>
            <span className="text-xs text-text-tertiary">
              {section.minWords?.toLocaleString()}-{section.maxWords?.toLocaleString()} kata
            </span>
          </div>
          <h4 className="text-text-primary font-medium mb-2">{section.title}</h4>
          <p className="text-text-secondary text-sm">{section.description}</p>
          
          {/* Additional Info */}
          {(section.keyEvents?.length > 0 || section.characters?.length > 0) && (
            <div className="mt-3 space-y-2">
              {section.keyEvents?.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-text-tertiary">Key Events:</span>
                  <p className="text-xs text-text-secondary">{section.keyEvents.join(', ')}</p>
                </div>
              )}
              {section.characters?.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-text-tertiary">Characters:</span>
                  <p className="text-xs text-text-secondary">{section.characters.join(', ')}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={onEdit}
            className="p-2 text-text-tertiary hover:text-text-secondary hover:bg-bg-primary rounded-lg transition-colors"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-text-tertiary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

OutlineSection.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

NovelOutlinePage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  onOutlineChange: PropTypes.func.isRequired,
  onStartGeneration: PropTypes.func.isRequired,
  onGenerateOutline: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  llmService: PropTypes.object.isRequired
}

export default NovelOutlinePage